// pkg125 — admin prop: Client Modules workspace renders this page with adminApi I/O; Submit + template download hidden in admin mode (D-124e) — delivered 2026-07-27
// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { adminApi } from '../utils/adminApi'; // pkg125 — admin mode
import { useFirmSettings } from '../contexts/FirmSettingsContext';

const SECTIONS = [
  { id: 'income', label: 'Income', isIncome: true, items: [
    'Salary','Interest Income from Investments','Spousal Support','Child Support',
    'Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
  { id: 'home', label: 'Home', items: [
    'Rent or Mortgage (incl. taxes & insurance)','Homeowners Association Dues','Lawn Care',
    'General Maintenance Costs','Exterminator','Housecleaning','Pool Maintenance',
    'Home Equity Loan','Snow Removal','Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
  { id: 'utilities', label: 'Utilities', items: [
    'Cable or Satellite','Home Phone','Mobile Phone','Water','Gas','Electricity',
    'Security System','Trash Removal','Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
  { id: 'transportation', label: 'Transportation', items: [
    'Auto Payment','Insurance','Fuel','Repair','State Registration','Public Transportation',
    'Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
  { id: 'taxes', label: 'Taxes, Fees & Related Costs', items: [
    'Quarterly Tax Payments','Professional Fees or Dues','Child Support Payments',
    'Spousal Support Payments','Support of Family / Non-Child Dependents',
    'Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
  { id: 'medical', label: 'Medical (Personal and Children)', items: [
    'Insurance Premium','Doctor Copayments','Prescription Copayments','Optometrist',
    'Physical Therapy/Care','Orthodontics','Dentistry','Mental Health Care',
    'Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
  { id: 'insurance', label: 'Insurance Premium Payments', items: [
    'Life','Disability','Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
  { id: 'childcare', label: 'Child Care', items: [
    'Tuition Payments','School Lunches','School Trips','Camps','Lessons',
    'Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
  { id: 'food', label: 'Food', items: [
    'Groceries','Dining Out','Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
  { id: 'clothing', label: 'Clothing / Personal Care', items: [
    'Clothing Purchases','School Uniform Purchases','Drycleaning/Laundry','Haircuts',
    'Gym Memberships','Club Dues','Classes/Lessons',
    'Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
  { id: 'petcare', label: 'Pet Care', items: [
    'Daycare','Boarding','Vet Fees','License Fees',
    'Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
  { id: 'entertainment', label: 'Entertainment', items: [
    'Travel','Movies','Miscellaneous 1','Miscellaneous 2','Miscellaneous 3','Miscellaneous 4',
  ]},
];

function fkey(sectionId, item, period) {
  return `${sectionId}__${item.replace(/\s+/g, '_')}__${period}`;
}

function sectionTotal(sectionId, items, values, period) {
  return items.reduce((s, item) => s + (parseFloat(values[fkey(sectionId, item, period)]) || 0), 0);
}

function fmt(n) {
  return (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

function AmountInput({ value, onChange }) {
  return (
    <input
      type="number" min="0" step="0.01"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder="0.00"
      style={{
        width: '100%', padding: '6px 8px', border: '1px solid #ddd',
        borderRadius: 3, fontSize: 13, fontFamily: 'Arial, sans-serif',
        textAlign: 'right', boxSizing: 'border-box',
      }}
    />
  );
}

// pkg125 — `admin` prop ({ clientId }): the Client Modules workspace renders
// this same page so the firm sees EXACTLY what the client sees. In admin mode
// all I/O goes through the /api/admin/clients/:id endpoints (adminApi), the
// Submit and template-download buttons are hidden (submission stays the
// client's act, D-124e), and Save is the only action.
export default function BudgetPage({ admin = null }) {
  const { firm } = useFirmSettings();
  const io = admin ? {
    get:  () => adminApi.getClientBudget(admin.clientId),
    save: (data) => adminApi.saveClientBudget(admin.clientId, data),
  } : {
    get:  () => api.getBudget(),
    save: (data) => api.saveBudget(data),
  };
  const [values, setValues]         = useState({});
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus]         = useState('');
  const [error, setError]           = useState('');

  useEffect(() => {
    io.get().then(rec => {
      if (rec?.data) setValues(rec.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  function set(k, v) { setValues(prev => ({ ...prev, [k]: v })); }

  async function handleSave() {
    setSaving(true); setStatus(''); setError('');
    try { await io.save(values); setStatus('Progress saved.'); }
    catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleSubmit() {
    setSubmitting(true); setStatus(''); setError('');
    try { await api.submitBudget(values); setStatus('Budget worksheet submitted to your attorney.'); }
    catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }

  async function handleDownloadTemplate() {
    try {
      const blob = await api.downloadBudgetTemplate();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'Divorce_Budget_Worksheet.xlsx';
      a.click();
    } catch (err) { setError('Download failed: ' + err.message); }
  }

  const incomeSec  = SECTIONS.find(s => s.isIncome);
  const expSecs    = SECTIONS.filter(s => !s.isIncome);
  const incPre     = sectionTotal(incomeSec.id, incomeSec.items, values, 'pre');
  const incPost    = sectionTotal(incomeSec.id, incomeSec.items, values, 'post');
  const expPre     = expSecs.reduce((s, sec) => s + sectionTotal(sec.id, sec.items, values, 'pre'), 0);
  const expPost    = expSecs.reduce((s, sec) => s + sectionTotal(sec.id, sec.items, values, 'post'), 0);
  const netPre     = incPre  - expPre;
  const netPost    = incPost - expPost;

  if (loading) return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;

  // ── Shared styles ──────────────────────────────────────────────────────
  const secHdr = {
    fontFamily: 'Arial, sans-serif', fontSize: 12, fontWeight: 700,
    padding: '6px 10px', background: '#2f5496', color: '#fff',
  };
  const totRow = {
    fontFamily: 'Arial, sans-serif', fontSize: 12, fontWeight: 700,
    padding: '5px 10px', background: '#bdd7ee', borderTop: '2px solid #2f5496',
  };
  const grandRow = {
    fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 700,
    padding: '7px 10px', background: '#0F1838', color: '#fff',
  };
  const netStyle = (val) => ({
    fontFamily: 'Arial, sans-serif', fontSize: 14, fontWeight: 700,
    padding: '8px 10px', background: val >= 0 ? '#0F1838' : '#B85042', color: '#fff',
  });

  function renderSection(sec) {
    const preTot  = sectionTotal(sec.id, sec.items, values, 'pre');
    const postTot = sectionTotal(sec.id, sec.items, values, 'post');
    return (
      <div key={sec.id} style={{ marginBottom: 4 }}>
        {/* Section header */}
        <div style={secHdr}>{sec.label}</div>

        {/* Column sub-headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', background: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
          <div style={{ padding: '4px 10px', fontSize: 10, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>Item</div>
          <div style={{ padding: '4px 6px', fontSize: 10, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase', textAlign: 'center' }}>Pre</div>
          <div style={{ padding: '4px 6px', fontSize: 10, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase', textAlign: 'center' }}>Post</div>
        </div>

        {/* Rows */}
        {sec.items.map(item => {
          const isMisc = item.startsWith('Miscellaneous');
          const kPre   = fkey(sec.id, item, 'pre');
          const kPost  = fkey(sec.id, item, 'post');
          const kLabel = fkey(sec.id, item, 'label');
          if (isMisc && !values[kLabel] && !parseFloat(values[kPre]) && !parseFloat(values[kPost])) return null;
          return (
            <div key={item} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', borderBottom: '1px solid #f5f5f5', background: '#fff' }}>
              <div style={{ padding: '4px 10px', fontFamily: 'Arial, sans-serif', fontSize: 12, display: 'flex', alignItems: 'center' }}>
                {isMisc ? (
                  <input type="text" value={values[kLabel] || ''} onChange={e => set(kLabel, e.target.value)}
                    placeholder="Describe..."
                    style={{ width: '100%', border: 'none', borderBottom: '1px dashed #ccc', background: 'transparent', fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#444', padding: '2px 0', outline: 'none' }} />
                ) : item}
              </div>
              <div style={{ padding: '3px 6px' }}><AmountInput value={values[kPre]}  onChange={v => set(kPre,  v)} /></div>
              <div style={{ padding: '3px 6px' }}><AmountInput value={values[kPost]} onChange={v => set(kPost, v)} /></div>
            </div>
          );
        })}

        {/* Section total */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px' }}>
          <div style={totRow}>Total</div>
          <div style={{ ...totRow, textAlign: 'right' }}>{fmt(preTot)}</div>
          <div style={{ ...totRow, textAlign: 'right' }}>{fmt(postTot)}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-title">Budget Worksheet</h1>
      <p className="page-subtitle">
        Enter monthly amounts for Pre-Divorce and Post-Divorce. Totals calculate automatically.
      </p>

      {status && <div className="alert alert-success">{status}</div>}
      {error  && <div className="alert alert-error">{error}</div>}

      {/* Summary totals */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Pre-Divorce Income',  val: incPre,  bg: '#2f5496' },
          { label: 'Post-Divorce Income', val: incPost, bg: '#0F1838' },
          { label: 'Pre-Divorce Net',     val: netPre,  bg: netPre  >= 0 ? '#0F1838' : '#B85042' },
          { label: 'Post-Divorce Net',    val: netPost, bg: netPost >= 0 ? '#0F1838' : '#B85042' },
        ].map(({ label, val, bg }) => (
          <div key={label} style={{ background: bg, borderRadius: 4, padding: '10px 12px', color: '#fff', textAlign: 'center' }}>
            <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'Arial, sans-serif' }}>{fmt(val)}</div>
          </div>
        ))}
      </div>

      {/* Column header bar */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', background: '#0F1838', color: '#fff', borderRadius: '4px 4px 0 0', marginBottom: 0 }}>
        <div style={{ padding: '8px 10px', fontSize: 11, fontFamily: 'Arial, sans-serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Category / Item</div>
        <div style={{ padding: '8px 6px', fontSize: 11, fontFamily: 'Arial, sans-serif', fontWeight: 700, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pre</div>
        <div style={{ padding: '8px 6px', fontSize: 11, fontFamily: 'Arial, sans-serif', fontWeight: 700, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Post</div>
      </div>

      {/* Income section */}
      {renderSection(incomeSec)}

      {/* Income total */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', marginBottom: 8 }}>
        <div style={grandRow}>Total Monthly Income</div>
        <div style={{ ...grandRow, textAlign: 'right' }}>{fmt(incPre)}</div>
        <div style={{ ...grandRow, textAlign: 'right' }}>{fmt(incPost)}</div>
      </div>

      {/* Expense sections */}
      {expSecs.map(sec => renderSection(sec))}

      {/* Expense total */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', marginBottom: 0 }}>
        <div style={grandRow}>Total Monthly Expenses</div>
        <div style={{ ...grandRow, textAlign: 'right' }}>{fmt(expPre)}</div>
        <div style={{ ...grandRow, textAlign: 'right' }}>{fmt(expPost)}</div>
      </div>

      {/* Net row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', marginBottom: 24, borderRadius: '0 0 4px 4px', overflow: 'hidden' }}>
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, fontWeight: 700, padding: '10px 10px', background: '#c9a84c', color: '#fff' }}>Income After Expenses</div>
        <div style={{ ...netStyle(netPre),  textAlign: 'right' }}>{fmt(netPre)}</div>
        <div style={{ ...netStyle(netPost), textAlign: 'right' }}>{fmt(netPost)}</div>
      </div>

      {/* Actions — admin mode: Save only (template download needs the client
          token; Submit is the client's act, D-124e) */}
      <div className="btn-row">
        {!admin && <button className="btn" onClick={handleDownloadTemplate}>⬇ Download Excel Template</button>}
        <button className="btn" onClick={handleSave} disabled={saving}>
          {saving ? <><span className="spinner" />Saving...</> : 'Save Progress'}
        </button>
        {!admin && (
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><span className="spinner" />Submitting...</> : 'Submit to Attorney'}
          </button>
        )}
      </div>
      {!admin && (
        <p style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', marginTop: 10 }}>
          Submitting sends a completed Excel spreadsheet to {firm.client_contact_email || 'the firm'} with all your figures and totals.
        </p>
      )}
    </div>
  );
}
